import { z } from "zod";
import { dateTimeSchema, nonEmptyStringSchema } from "./common.js";
import { processingJobTypeSchema } from "./processing.js";
import { visibilityClassSchema } from "./vocabulary.js";
/**
 * Operational notification kinds. Closed union on purpose: wiring a real
 * transport later must not widen what a notification may describe. Both kinds
 * describe an operator/user-facing condition — never content.
 *
 * - `reauthorization_required`: a provider credential can no longer be used and
 *   the owner has to re-consent before sync can resume.
 * - `sync_permanently_failed`: a sync run/pipeline job exhausted its attempts or
 *   was poisoned and is now dead-lettered.
 */
export const notificationKindSchema = z.enum([
    "reauthorization_required",
    "sync_permanently_failed",
]);
/**
 * Redaction boundary for a notification code. A notification may only carry a
 * bounded, lowercase-safe code — never a raw provider error, token, query text
 * or canonical content. The pattern mirrors the worker runtime's safe-code
 * guard, so a value that could smuggle credential/surface content is rejected.
 */
export const notificationSafeCodeSchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9_]{0,127}$/u);
/**
 * Opaque, typed internal record reference (e.g. a hashed job id). Bounded and
 * pattern-restricted so a free-form value or credential cannot ride along as
 * the "reference".
 */
export const notificationReferenceSchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9_-]{0,127}$/u);
// Notification identity fields route the alert; they are opaque internal ids at
// this boundary (the worker layer holds identities as plain strings), so a
// bounded non-empty string is validated rather than a branded UUID.
const notificationIdentitySchema = nonEmptyStringSchema.max(200);
export const notificationEventSchema = z
    .object({
    kind: notificationKindSchema,
    tenantId: notificationIdentitySchema,
    ownerMemberId: notificationIdentitySchema,
    visibility: visibilityClassSchema,
    // Which part of the pipeline stopped. A closed enum of internal stage names
    // — no content, no free text — and the field that makes a notification
    // *closable*: recovery is observed per stage, so a record cannot be cleared
    // by a success somewhere unrelated. It also lets a reader group blockers by
    // the plain-language group the stage belongs to without naming the stage.
    stage: processingJobTypeSchema,
    safeCode: notificationSafeCodeSchema,
    reference: notificationReferenceSchema,
    occurredAt: dateTimeSchema,
})
    .strict();
/**
 * What closes an open notification: a tenant's pipeline stage produced a
 * success again. Deliberately not "the same job succeeded" — the job that
 * failed is dead-lettered and will not run again, so waiting for it would keep
 * a resolved blocker open forever. The signal that the *condition* passed is
 * the next success anywhere in that stage for that tenant.
 */
export const notificationResolutionSchema = z
    .object({
    tenantId: notificationIdentitySchema,
    stage: processingJobTypeSchema,
    resolvedAt: dateTimeSchema,
})
    .strict();
//# sourceMappingURL=notification.js.map